/* eslint-disable react/prop-types */
import React, { useMemo } from 'react';
import { useSelector } from 'react-redux';
import styled from '@emotion/styled';
import moment from 'moment-timezone';
import UnifiedDayHeader from './UnifiedDayHeader';
import MonthDayContent from './MonthDayContent';
import Text from '../../../../shared/components/Text/Text';

const MonthContainer = styled('div')`
  display: flex;
  flex-direction: column;
  gap: 8px;
  padding: 8px;
  background-color: #EEEEEE;
  height: 100%;
  width: 100%;
  padding-bottom: 35px;
  overflow: scroll;
`;

const WeekRow = styled('div')`
  display: flex;
  flex-direction: row;
  gap: 8px;
  flex: 1;
`;

const DayCell = styled('div')`
  display: flex;
  flex-direction: column;
  flex: 1 1 0%;
  min-width: 220px;
  background: white;
  border-radius: 4px;
  min-height: 200px;
  overflow: hidden;
  opacity: ${(props) => (props.isPaddingDay ? 0.5 : 1)};
  transition: opacity 0.2s ease;

  &:hover {
    opacity: ${(props) => (props.isPaddingDay ? 0.7 : 1)};
  }
`;

const StatsBar = styled('div')`
  background: white;
  min-height: 40px;
  border-top: 1px solid #EEEEEE;
  width: 100%;
  justify-content: center;
  align-items: center;
  display: flex;
  margin-top: auto;
`;

const StatsBarContent = styled('div')`
  display: flex;
  flex-direction: row;
  align-items: center;
  justify-content: center;
  width: 100%;
  gap: 16px;
`;

const StatsBarItem = styled('div')`
  display: flex;
  flex-direction: row;
  align-items: center;
  gap: 4px;
`;

const MonthView = ({ isMonthItemsLoading }) => {
  const selectedDate = useSelector((state) => state.calendar.data.selectedDate);
  const calendarItems = useSelector((state) => state.calendar.data.calendarItems);
  const programType = useSelector((state) => state.calendar.data.programType);

  // Generate full calendar grid with padding days from adjacent months
  const weeks = useMemo(() => {
    // Calculate the calendar grid boundaries
    const monthStart = moment(selectedDate).startOf('month');
    const gridStart = monthStart.clone().startOf('week'); // First Sunday on or before month start
    const gridEnd = moment(selectedDate).endOf('month').endOf('week'); // Last Saturday on or after month end

    // Calculate total number of days in the grid
    const totalDays = gridEnd.diff(gridStart, 'days') + 1;

    // Generate all dates in the grid
    const allDates = [];
    for (let i = 0; i < totalDays; i += 1) {
      allDates.push(gridStart.clone().add(i, 'days'));
    }

    // Split into weeks of 7 days each
    const numWeeks = totalDays / 7; // Will be 4, 5, or 6
    const result = [];
    for (let i = 0; i < numWeeks; i += 1) {
      result.push(allDates.slice(i * 7, (i + 1) * 7));
    }

    return result;
  }, [selectedDate]);

  const isCurrentMonthDay = (date) => date.month() === moment(selectedDate).month();

  return (
    <MonthContainer>
      {weeks.map((week) => (
        <WeekRow key={week[0].format('YYYY-MM-DD')}>
          {week.map((date) => {
            const dateKey = date.format('YYYY-MM-DD');
            const dayData = calendarItems[dateKey];
            const isPaddingDay = !isCurrentMonthDay(date);

            return (
              <DayCell key={dateKey} isPaddingDay={isPaddingDay}>
                <UnifiedDayHeader
                  programType={programType}
                  viewType='monthView'
                  date={date}
                  isExpanded={false}
                  onToggleExpand={null}
                  showWorkoutName
                  isWeekView={false}
                />
                <MonthDayContent
                  date={date}
                  exercises={dayData?.exercises || []}
                  isLoading={isMonthItemsLoading}
                />
                <StatsBar>
                  <StatsBarContent>
                    <StatsBarItem>
                      <Text fontSize='13px' color='#BDBDBD'>Sets</Text>
                      <Text
                        marginLeft='5px'
                        marginTop='2px'
                        fontSize='13px'
                        color='#616161'
                        fontWeight='500'
                      >
                        {dayData?.setCount || 0}
                      </Text>
                    </StatsBarItem>
                    <StatsBarItem>
                      <Text fontSize='13px' color='#BDBDBD'>Reps</Text>
                      <Text
                        marginLeft='5px'
                        marginTop='2px'
                        fontSize='13px'
                        color='#616161'
                        fontWeight='500'
                      >
                        {dayData?.repCount || 0}
                      </Text>
                    </StatsBarItem>
                  </StatsBarContent>
                </StatsBar>

              </DayCell>
            );
          })}
        </WeekRow>
      ))}
    </MonthContainer>
  );
};

export default MonthView;
